import { Link } from "react-router-dom";
import { ArrowRight, Mic, ShieldCheck, FileSignature, Receipt, Settings, PackageOpen, HeartHandshake, ClipboardList, ClipboardCheck, CheckCircle2, Globe } from "lucide-react";
import Logo from "@/components/Logo";
import LaunchPricingBadge from "@/components/marketing/LaunchPricingBadge";
import { isLandingV2Enabled } from "@/lib/leviLandingV2Flag";
import LandingV2 from "@/components/landing/LandingV2";
import { SigninLink } from "@/components/common/SigninLink";

// iter37 · Rio-approved "What you get" section — the 9 real contractor
// workflow surfaces shipped today. Marketing leaks like "change-order
// invoices" are removed; every card maps to a live route. Held/frozen
// features (Stripe live, Resend live, GHL, SMS, DNS, GPT Vision, receipt
// scanning, LiDAR/Levi Scan, Change Orders, Customer Selections, public
// crew packet sharing, customer-facing Levi, automated takeoffs) are
// NOT mentioned — the iter37 regression test forbids them by string.
const features = [
  {
    icon: Settings,
    title: "Contractor Pricing Profile",
    desc: "Labor rates, markup, overhead, profit, trip charge, tax, and deposit settings stay contractor-controlled.",
  },
  {
    icon: PackageOpen,
    title: "Material Price Book",
    desc: "Save supplier, product, unit, cost, coverage, waste, and markup details so estimates use your numbers, not guesses.",
  },
  {
    icon: Mic,
    title: "Job Walk → Organized Scope",
    desc: "Voice or typed notes become structured scope, suggested line items, materials, labor, assumptions, and risks. All AI suggestions are contractor-reviewed before use.",
  },
  {
    icon: ShieldCheck,
    title: "Estimate Editor + Double Check",
    desc: "Review missing pricing, missing labor, missing material, cleanup, demo, disposal, access notes, overhead, and risk before sending.",
  },
  {
    icon: HeartHandshake,
    title: "Levi AI Project Manager",
    desc: "Levi is your contractor-side AI Project Manager for job intake, estimating guidance, review items, missing pricing, missing scope, risks, and next steps — while keeping the contractor in control.",
  },
  {
    icon: FileSignature,
    title: "Proposals + Contracts",
    desc: "Turn approved scope and pricing into polished customer-facing proposal and contract-ready drafts.",
  },
  {
    icon: ClipboardList,
    title: "Work Orders / Crew Packets",
    desc: "Contractor-internal packets with scope, tasks, work areas, quantities, material needs, access notes, and completion checklists — no pricing, margin, or profit exposed.",
  },
  {
    icon: Receipt,
    title: "Invoices + Customer Updates",
    desc: "Deposit, progress, and final invoices, plus contractor-reviewed customer updates in English & Spanish.",
  },
  {
    icon: ClipboardCheck,
    title: "Closeout / Warranty Packet",
    desc: "Completion notes, final approval, final invoice, contractor-controlled workmanship warranty details, manufacturer/product warranty info, maintenance notes, and customer responsibilities.",
  },
];

const Landing = () => {
  // Gated production V2 (approved six-section Features composition,
  // extracted into frontend/src/components/landing/*). Default OFF —
  // V1 stays live. Opt-in for QA via `?leviLandingV2=1` URL param,
  // `window.__leviLandingV2Override`, or the
  // REACT_APP_LEVI_LANDING_V2_ENABLED env var.
  if (isLandingV2Enabled()) {
    return <LandingV2 />;
  }
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-[var(--jc-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 md:h-24 flex items-center justify-between gap-3">
          <div className="flex items-center min-w-0">
            <Logo
              variant="full"
              theme="dark"
              to="/"
              size="lg"
              className="hidden md:inline-flex"
              data-testid="landing-logo"
            />
            {/* Mobile: mark + inline text wordmark (approved mark PNG + brand text) */}
            <Link
              to="/"
              data-testid="landing-logo-mobile"
              aria-label="JobClose AI"
              className="inline-flex md:hidden items-center gap-2 min-w-0"
            >
              <img
                src="/brand/jobclose-ai-approved-logo-mark.png"
                alt=""
                style={{ height: 44, width: "auto" }}
                className="shrink-0 select-none"
                draggable={false}
              />
              <span className="font-display text-xl tracking-tight text-[var(--jc-text)] whitespace-nowrap leading-none">
                JobClose <span className="text-[var(--jc-blue)]">AI</span>
              </span>
            </Link>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-[var(--jc-text-soft)]">
            <a href="#how" className="hover:text-[var(--jc-text)]">How it works</a>
            <a href="#features" className="hover:text-[var(--jc-text)]">Features</a>
            <a href="#pricing" className="hover:text-[var(--jc-text)]">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <SigninLink
              data-testid="landing-login-btn"
              className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-semibold text-[var(--jc-text)] hover:text-[var(--jc-blue)]"
            >
              Sign in
            </SigninLink>
            <Link
              to="/signup"
              data-testid="landing-signup-btn"
              className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-md jc-btn-primary whitespace-nowrap"
            >
              Schedule a Demo <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#FAF7F2] via-white to-[#F4EFE7] text-[var(--jc-text)]">
        {/* Desktop: full-bleed contractor photo anchored to the right side of the hero,
            masked so it fades softly into the warm cream copy area on the left.
            The image is decorative in this layout — its alt text lives on the mobile <img> below. */}
        <div
          aria-hidden="true"
          className="hidden lg:block absolute inset-y-0 right-0 w-[62%] pointer-events-none"
          style={{
            backgroundImage: "url('/brand/hero-contractor-floor-measure-v1.png')",
            backgroundSize: "cover",
            backgroundPosition: "center right",
            backgroundRepeat: "no-repeat",
            maskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 12%, rgba(0,0,0,0.85) 26%, #000 40%)",
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 12%, rgba(0,0,0,0.85) 26%, #000 40%)",
          }}
        />
        <div className="absolute inset-0 pointer-events-none opacity-[0.35] bg-[radial-gradient(circle_at_15%_20%,rgba(37,99,235,0.06),transparent_55%),radial-gradient(circle_at_85%_75%,rgba(15,118,110,0.05),transparent_50%)]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          <div className="lg:col-span-6 xl:col-span-6 relative z-10">
            <div
              data-testid="hero-founder-trust-line"
              className="text-[13px] font-medium tracking-[0.14em] uppercase text-[var(--jc-blue)] mb-5"
            >
              Built by a contractor, for contractors.
            </div>
            <h1
              data-testid="hero-headline"
              className="font-display text-5xl lg:text-7xl leading-[0.95] tracking-tight text-[var(--jc-text)]"
            >
              Your pricing.<br />
              Our <span className="text-[var(--jc-blue)]">AI</span>.<br />
              Better estimates.
            </h1>
            <div className="mt-7 space-y-4 max-w-xl text-base sm:text-lg text-[var(--jc-text-soft)] leading-relaxed" data-testid="hero-supporting-copy">
              <p>
                After years of paying for one app to estimate jobs, another for contracts, and others to schedule and manage the work, I decided to build something better.
              </p>
              <p>
                JobClose AI brings the tools contractors need into one affordable platform. With Levi, your AI project manager, you can organize estimates, catch missed items, prepare professional documents, and keep every job moving&mdash;while you stay in control of every important decision.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/signup"
                data-testid="hero-signup-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md font-semibold bg-[var(--jc-blue)] text-white hover:bg-[var(--jc-blue-700)] transition shadow-sm"
              >
                Schedule a Demo <ArrowRight size={16} />
              </Link>
              <SigninLink
                data-testid="hero-signin-btn"
                className="inline-flex items-center px-6 py-3.5 rounded-md font-semibold border border-[var(--jc-border-strong)] text-[var(--jc-text)] bg-white hover:bg-[#FAF7F2] transition"
              >
                Sign in
              </SigninLink>
            </div>
            <LaunchPricingBadge className="mt-8 max-w-xl" />
          </div>

          {/* Right column: on mobile it holds the visible photograph + Levi card.
              On desktop the photograph is painted by the full-bleed layer above,
              and this column only anchors the Levi Double Check card at the bottom-right. */}
          <div className="lg:col-span-6 xl:col-span-6 relative min-h-[420px] lg:min-h-[560px]">
            <img
              src="/brand/hero-contractor-floor-measure-v1.png"
              alt="Residential remodeling contractor kneeling on a jobsite floor, speaking into a phone while using a laser measurement device that projects a single red laser beam across the floor"
              className="block lg:hidden w-full h-auto rounded-2xl ring-1 ring-black/5 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.25)]"
            />
            <div
              data-testid="hero-levi-double-check-card"
              className="mt-4 lg:mt-0 lg:absolute lg:bottom-2 lg:right-0 lg:w-[380px] w-full bg-white border border-[var(--jc-border)] rounded-xl p-4 shadow-[0_18px_40px_-18px_rgba(15,23,42,0.35)] text-[var(--jc-text)]"
            >
              <div className="flex items-start gap-3">
                <img
                  src="/levi/levi-badge.webp"
                  alt=""
                  aria-hidden="true"
                  className="w-14 h-14 rounded-full shrink-0 select-none ring-1 ring-[var(--jc-border)]"
                  draggable={false}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg leading-tight mb-2">Levi Double Check</div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[var(--jc-green)] shrink-0" />
                      <span>Pricing check passed</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[var(--jc-yellow)] shrink-0" />
                      <span>Labor may be low</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[var(--jc-red)] shrink-0" />
                      <span>Missing disposal line item</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[var(--jc-border)] text-sm text-[var(--jc-text-soft)]">
                You review and approve every change.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works — VIS-002 approved 3-step flow + Built By Contractor CTA.
          Assets rendered exactly as provided; no CSS/SVG/icon reconstruction. */}
      <section id="how" data-testid="landing-how-it-works" className="text-[var(--jc-text)]">
        {/* OPENING JOB-WALK BAND — approved dark hero at the top of How It Works,
            per the approved VIS-002 mockup: stacked "Win / Run / Close" headline
            on the left with supporting copy and "Levi recommends. You decide."
            trust chip; approved contractor/job-walk production asset on the right.
            (The upstream VIS-001 hero above this section remains locked and
            untouched — this is the mockup's opening band, added as a distinct
            new band inside #how.) */}
        <div className="bg-[var(--jc-navy)] text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-6" data-testid="how-opening-scene">
              <h2 className="font-display text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                Win the job.<br />
                <span className="text-[var(--jc-blue)]">Run the job.</span><br />
                <span className="text-[var(--jc-blue)]">Close the job.</span>
              </h2>
              <p className="mt-6 text-white/85 leading-relaxed max-w-md">
                From job walk to final payment, JobClose AI keeps everything connected&mdash;so you stay in control and get paid.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-[var(--jc-blue)] font-semibold">
                <CheckCircle2 size={18} aria-hidden="true" />
                <span>Levi recommends. You decide.</span>
              </div>
            </div>
            <div className="lg:col-span-6">
              <img
                src="/brand/hero-contractor-floor-measure-v1.png"
                alt="Contractor on a jobsite kneeling with a laser measurement device and speaking into a phone during a job walk."
                className="w-full h-auto block rounded-xl"
                data-testid="how-opening-scene-image"
              />
            </div>
          </div>
        </div>

        {/* STEP 1 — Win the job. (light) */}
        <div className="bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14 grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            <div className="lg:col-span-3" data-testid="how-step-1">
              <div className="text-[var(--jc-blue)] font-display text-3xl leading-none mb-4">1 &mdash;</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Win the job.
              </h2>
              <p className="mt-5 text-[var(--jc-text-soft)] leading-relaxed max-w-sm">
                Stop making customers wait hours&mdash;or days&mdash;for a proposal. Levi builds your scope, material takeoffs, and estimate <span className="font-semibold text-[var(--jc-text)]">in minutes</span> using your pricing.
              </p>
              <ul className="mt-6 space-y-4 max-w-sm">
                {[
                  { label: "Capture naturally", desc: "Speak, photograph, measure, and add notes." },
                  { label: "Levi Vision builds the scope", desc: "Photos and measurements become an organized scope and material takeoff." },
                  { label: "Win jobs faster", desc: "Deliver professional proposals in minutes instead of making customers wait hours\u2014or even days." },
                ].map((b) => (
                  <li key={b.label} className="flex gap-3">
                    <CheckCircle2 size={18} className="text-[var(--jc-blue)] shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <div className="font-semibold text-[var(--jc-text)]">{b.label}</div>
                      <div className="text-sm text-[var(--jc-text-soft)] leading-relaxed">{b.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-9">
              <img
                src="/brand/VIS-002_HowItWorks_Win-The-Job-Tablet_v1.png"
                alt="JobClose AI dashboard on a tablet showing the Job Walk, Estimate, and Review flow for 2405 Maple Ave, with Levi Vision producing a Scope & Takeoff, an Estimate Built panel with itemized pricing, and a Levi Double Check callout."
                className="w-full h-auto block object-contain"
                data-testid="how-step-1-image"
              />
            </div>
          </div>
        </div>

        {/* Language belt */}
        <div className="bg-[#F4F6FB] border-y border-[var(--jc-border)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
            <div className="flex items-start md:items-center gap-3 flex-1" data-testid="how-language-belt">
              <div className="w-10 h-10 rounded-xl bg-white ring-1 ring-[var(--jc-border)] flex items-center justify-center shrink-0" aria-hidden="true">
                <Globe size={18} className="text-[var(--jc-blue)]" />
              </div>
              <div>
                <div className="font-semibold text-[var(--jc-text)]">Full English &amp; Spanish experience.</div>
                <div className="text-sm text-[var(--jc-text-soft)] leading-relaxed">
                  Speak, estimate, and send documents in the language you and your customers prefer.
                </div>
              </div>
            </div>
            <div className="text-sm text-[var(--jc-text-soft)] md:text-right md:max-w-[220px] shrink-0">
              App can be moved from<br className="hidden md:inline" /> English to Spanish anytime.
            </div>
          </div>
        </div>

        {/* STEP 2 — Run the job. (dark) */}
        <div className="bg-[var(--jc-navy)] text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14 grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            <div className="lg:col-span-3" data-testid="how-step-2">
              <div className="text-[var(--jc-blue)] font-display text-3xl leading-none mb-4">2 &mdash;</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Run the job.
              </h2>
              <p className="mt-5 text-white/80 leading-relaxed max-w-sm">
                Contracts, scheduling, crew packets, material takeoffs, customer updates, and changes&mdash;everything stays connected to the project and always up to date.
              </p>
            </div>
            <div className="lg:col-span-9">
              <img
                src="/brand/VIS-002_HowItWorks_Run-The-Job-Tablet_v1.png"
                alt="Contractor holding a tablet on a jobsite reviewing the Customer Updates tab for 2405 Maple Ave, with a photo of a roof installation in progress and an Everything is on track update."
                className="w-full h-auto block object-contain"
                data-testid="how-step-2-image"
              />
            </div>
          </div>
        </div>

        {/* STEP 3 — Close the job. (light) */}
        <div className="bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14 grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            <div className="lg:col-span-3" data-testid="how-step-3">
              <div className="text-[var(--jc-blue)] font-display text-3xl leading-none mb-4">3 &mdash;</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Close the job.
              </h2>
              <p className="mt-5 text-[var(--jc-text-soft)] leading-relaxed max-w-sm">
                Get paid, deliver a professional closeout package, review profitability, request a review, and keep a complete record of the job.
              </p>
              <p className="mt-4 text-[var(--jc-text-soft)] leading-relaxed max-w-sm">
                Customers remember that.<br />And you get paid.
              </p>
            </div>
            <div className="lg:col-span-9">
              <img
                src="/brand/VIS-002_HowItWorks_Close-The-Job-Workflow_v1.png"
                alt="Close the job workflow: Invoice Paid, Closeout Delivered, Profitability Reviewed, Review Requested, and Project Archived, with a Levi Double Check note about disposal cost being higher than expected."
                className="w-full h-auto block object-contain"
                data-testid="how-step-3-image"
              />
            </div>
          </div>
        </div>

        {/* Built By A Contractor CTA — approved Levi Founder CTA asset restored
            per PO instruction; founder statement left, Levi visual anchor and
            enlarged CTA on the right, all inside one cohesive dark panel. */}
        <div className="bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-14 lg:pb-16">
            <div className="rounded-2xl bg-[var(--jc-navy)] text-white p-8 lg:p-12 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
              <div className="lg:col-span-7" data-testid="how-builtby-copy">
                <div className="flex items-start gap-4">
                  <span aria-hidden="true" className="font-display text-7xl leading-none text-[var(--jc-blue)]/80 select-none -mt-2">&ldquo;</span>
                  <div>
                    <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-[var(--jc-blue)] mb-3">
                      Built by a contractor.
                    </div>
                    <p className="text-white/90 leading-relaxed text-xl lg:text-2xl max-w-2xl">
                      I spent thousands on estimating software that didn&apos;t work the way contractors actually price and run jobs.{" "}
                      <span className="text-[var(--jc-blue)] font-semibold">So I built JobClose AI.</span>
                    </p>
                    <Link
                      to="/signup"
                      data-testid="how-request-demo-btn"
                      className="mt-7 inline-flex items-center gap-2 px-7 py-4 rounded-md font-semibold text-base bg-[var(--jc-blue)] text-white hover:bg-[var(--jc-blue-700)] transition shadow-sm"
                    >
                      Request a Demo <ArrowRight size={18} />
                    </Link>
                    <div className="mt-3 text-xs text-white/70 max-w-[280px]">
                      See how JobClose AI works on a real contractor project.
                    </div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-5 flex justify-center lg:justify-end" data-testid="how-builtby-levi">
                <img
                  src="/brand/VIS-002_HowItWorks_Levi-Success_v1.png"
                  alt="Levi, the JobClose AI mascot, seated and smiling with the approved JobClose AI hat, collar, and Levi name tag."
                  className="w-full max-w-xs lg:max-w-sm h-auto select-none"
                  draggable={false}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-white border-y border-[var(--jc-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-3xl">
            <div className="overline mb-3">What you get</div>
            <h2 className="font-display text-3xl lg:text-5xl tracking-tight" data-testid="landing-what-you-get-headline">
              One workflow from <span className="text-[var(--jc-teal)]">job walk</span> to closeout.
            </h2>
            <p className="mt-5 text-[var(--jc-text-soft)]" data-testid="landing-what-you-get-subhead">
              JobClose AI helps contractors turn job notes, measurements, pricing rules, and approved scope into estimates, proposals, contracts, crew packets, invoices, customer updates, and closeout documents — while keeping pricing and approval in the contractor&apos;s control.
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div
                key={f.title}
                data-testid={`landing-feature-card-${i + 1}`}
                className="jc-card p-6 jc-card-hover"
              >
                <div className="w-10 h-10 rounded-md bg-[var(--jc-navy)] text-white flex items-center justify-center mb-4">
                  <f.icon size={18} />
                </div>
                <div className="font-display text-xl">{f.title}</div>
                <p className="text-sm text-[var(--jc-text-soft)] mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center">
          <div className="overline mb-3">Simple pricing</div>
          <h2 className="font-display text-3xl lg:text-5xl tracking-tight">$129/month · 2 full users included</h2>
          <p className="mt-4 text-[var(--jc-text-soft)] max-w-2xl mx-auto">
            Your monthly plan covers the software with two full users included. Add teammates any time at $20/month per additional active user. Setup helps us configure your labor, materials, markup, and first workflow correctly.
          </p>
        </div>

        {/* Monthly plans — LOCKED launch pricing (2026-08-24) */}
        <div className="mt-12 grid md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-testid="landing-monthly-plans">
          {[
            { id: "team",   name: "JobClose AI",              price: "$129", users: "2 full users included · +$20/mo per additional active user · 7-day free trial", highlighted: true },
            { id: "ent",    name: "Larger Teams / Enterprise", price: "Custom", users: "Volume user pricing", custom: true },
          ].map((t) => (
            <div
              key={t.id}
              data-testid={`landing-plan-${t.id}`}
              className={`jc-card p-6 relative flex flex-col ${t.highlighted ? "border-2 border-[var(--jc-blue)] shadow-lg" : ""}`}
            >
              {t.highlighted && (
                <div className="absolute -top-3 left-6 px-2 py-0.5 rounded-full bg-[var(--jc-blue)] text-white text-[10px] font-bold tracking-wider">MOST POPULAR</div>
              )}
              <div className="overline">{t.name}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-display text-4xl">{t.price}</span>
                {!t.custom && <span className="text-sm text-[var(--jc-text-soft)]">/mo</span>}
              </div>
              <div className="text-xs text-[var(--jc-text-soft)] mt-1">{t.users}</div>
            </div>
          ))}
        </div>

        {/* One-time setup & onboarding */}
        <div className="mt-20" data-testid="landing-setup-section">
          <div className="text-center">
            <div className="overline mb-3">One-time setup &amp; onboarding</div>
            <h3 className="font-display text-2xl lg:text-4xl tracking-tight">Get set up right — no surprise fees</h3>
            <p className="mt-4 text-[var(--jc-text-soft)] max-w-2xl mx-auto">
              Choose a setup package that matches your team size, price book, and workflow. Your monthly plan is separate.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            {[
              {
                id: "basic",
                name: "Basic Setup",
                price: "$249",
                desc: "Best for a solo contractor with a simple pricing profile and a small starting material list.",
              },
              {
                id: "pro",
                name: "Pro Setup",
                price: "$399",
                desc: "Best for contractors who want help organizing labor, markup, common materials, proposal settings, and first estimate setup.",
                highlighted: true,
              },
              {
                id: "premium",
                name: "Premium Setup",
                price: "starting at $799",
                desc: "Best for larger price books, vendor spreadsheets, multiple trades, team setup, and done-for-you onboarding.",
              },
              {
                id: "custom",
                name: "Custom Setup",
                price: "quoted upfront",
                desc: "For larger teams, complex workflows, large material imports, or custom rollout needs.",
              },
            ].map((s) => (
              <div
                key={s.id}
                data-testid={`landing-setup-${s.id}`}
                className={`jc-card p-6 flex flex-col ${s.highlighted ? "border-2 border-[var(--jc-teal)] shadow-lg" : ""}`}
              >
                <div className="overline">{s.name}</div>
                <div className="mt-2 font-display text-3xl">{s.price}</div>
                <div className="text-xs uppercase tracking-wider text-[var(--jc-text-soft)] mt-1">One-time</div>
                <p className="mt-4 text-sm text-[var(--jc-text-soft)] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center" data-testid="landing-setup-trust">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jc-bg)] border border-[var(--jc-border)] text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--jc-teal)]" aria-hidden="true" />
              <span className="font-semibold text-[var(--jc-text)]">Setup is always reviewed before onboarding. No surprise setup fees.</span>
            </div>
          </div>

          {/* Optional Add-On */}
          <div className="mt-12" data-testid="landing-website-addon">
            <div className="text-center mb-5">
              <div className="overline">Optional Add-On</div>
            </div>
            <div className="jc-card p-6 max-w-3xl mx-auto border-2 border-[var(--jc-teal)]/30">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-display text-2xl">Contractor Website Starter</div>
                  <div className="font-mono-jc text-lg text-[var(--jc-teal)] mt-1">starting at $499 setup</div>
                </div>
                <span className="shrink-0 inline-block self-start px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-[var(--jc-teal)]/10 text-[var(--jc-teal)] border border-[var(--jc-teal)]/30">
                  Optional
                </span>
              </div>
              <p className="mt-4 text-sm text-[var(--jc-text-soft)] leading-relaxed">
                A simple contractor website with your services, service area, contact info, and request-estimate form.
              </p>
              <p className="mt-3 text-xs text-[var(--jc-text-soft)] leading-relaxed">
                Includes first 12 months of hosting with an active JobClose AI subscription. Hosting and maintenance renew annually after year one. Custom domains, SEO, ads, multi-page websites, advanced edits, and custom copywriting are quoted separately.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-14 text-center">
          <Link
            to="/signup"
            data-testid="pricing-cta-btn"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md font-semibold jc-btn-primary"
          >
            Create your account <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[var(--jc-border)] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-[var(--jc-text-soft)]">
          <div>© {new Date().getFullYear()} JobClose AI · Built by a contractor, for contractors.</div>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-[var(--jc-text)]" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-[var(--jc-text)]" data-testid="footer-terms">Terms</Link>
            <Link to="/data-security" className="hover:text-[var(--jc-text)]" data-testid="footer-security">Data Security</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
